import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import IconButton from 'LuxKit/Button/IconButton'
import LineCornerDownRightIcon from 'LuxKit/Icons/line/LineCornerDownRightIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'

const chipIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(${rem(12)}) scale(0.94);
  }
  60% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: none;
  }
`

// One-time glow pulse, timed so it lands as the AiContextTrace comet arrives,
// holding the eye on the freshly-added context for a beat.
const chipPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 var(--chat-context-glow);
  }
  70% {
    box-shadow: 0 0 0 ${rem(12)} transparent;
  }
  100% {
    box-shadow: 0 0 0 ${rem(12)} transparent;
  }
`

const shine = keyframes`
  0% {
    transform: translateX(var(--chat-context-shine-start-x));
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  100% {
    transform: translateX(var(--chat-context-shine-end-x));
    opacity: 0;
  }
`

const ChatInputContextBarContainer = styled.div`
  --chat-context-glow: ${props => props.theme.palette.product.ai.light35};
  --chat-context-shine-start-x: -120%;
  --chat-context-shine-end-x: 120%;

  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  border-radius: ${props => props.theme.borderRadius.L};
  padding: ${rem(12)} ${rem(20)} ${rem(20)} ${rem(20)};
  margin-bottom: ${rem(-8)};
  position: relative;
  overflow: hidden;
  z-index: 0;
  animation:
    ${chipIn} 500ms cubic-bezier(0.18, 1.1, 0.3, 1) both,
    ${chipPulse} 1800ms ease-out 1;

  [dir="rtl"] & {
    --chat-context-shine-start-x: 120%;
    --chat-context-shine-end-x: -120%;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: inherit;
    background: linear-gradient(
      105deg,
      transparent 30%,
      rgba(255, 255, 255, 0.6) 48%,
      transparent 66%
    );
    transform: translateX(var(--chat-context-shine-start-x));
    animation: ${shine} 1000ms ease 150ms 1;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    &::after {
      animation: none;
      content: none;
    }
  }
`

interface ChatInputContextBarProps {
  label: string
  onClose: () => void
}

function ChatInputContextBar({ label, onClose }: ChatInputContextBarProps) {
  return (
    <ChatInputContextBarContainer>
      <Group direction="horizontal" gap={12} verticalAlign="center">
        <LineCornerDownRightIcon rtlFlip />
        <BodyText variant="medium">
          {label}
        </BodyText>
      </Group>
      <IconButton kind="tertiary" size="small" onClick={onClose}>
        <LineTimesIcon />
      </IconButton>
    </ChatInputContextBarContainer>
  )
}

export default ChatInputContextBar
